import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/** Resets scroll on route change (hash links are left to their own handlers). */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/**
 * Fades content in as it scrolls into view.
 * Content is visible by default (pre-render, no-JS, reduced-motion);
 * it is only hidden after hydration when it starts below the fold.
 */
export function Reveal({ children, delay = 0, className = "", as: Tag = "div" }) {
  const ref = useRef(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    // Never hide content while react-snap pre-renders: the snapshot must contain visible markup.
    if (/ReactSnap/i.test(navigator.userAgent)) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.92) return; // already on screen
    setHidden(true);
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHidden(false);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${hidden ? "reveal-hidden" : ""} ${className}`}
      style={{ transitionDelay: hidden ? "0ms" : `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/** Navy hero band used at the top of every interior page. */
export function PageHeader({ eyebrow, title, children, actions }) {
  return (
    <header className="on-dark noise relative overflow-hidden bg-navy-950 text-white">
      <div className="bg-grid-dark absolute inset-0 [mask-image:radial-gradient(ellipse_at_30%_0%,black,transparent_70%)]" aria-hidden="true" />
      <div className="glow-blue absolute -right-40 -top-40 h-[34rem] w-[34rem] animate-drift opacity-60" aria-hidden="true" />
      <div className="glow-teal absolute -bottom-52 left-1/4 h-[30rem] w-[30rem] animate-drift opacity-40 [animation-delay:-6s]" aria-hidden="true" />
      <div className="container-x relative pb-20 pt-[8.5rem] md:pb-32 md:pt-[12rem]">
        <div className="max-w-4xl">
          {eyebrow && (
            <p className="eyebrow mb-7 flex items-center gap-3">
              <span className="h-px w-8 bg-brand-light" aria-hidden="true" />
              {eyebrow}
            </p>
          )}
          <h1 className="h-display">{title}</h1>
          {children && <p className="lede mt-7 max-w-2xl">{children}</p>}
          {actions && <div className="mt-10 flex flex-col gap-3 sm:flex-row">{actions}</div>}
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-light/50 to-transparent" aria-hidden="true" />
    </header>
  );
}

/** Index + eyebrow + heading + optional lede, consistent across sections. */
export function SectionHeading({ index, eyebrow, title, children, align = "left", dark = false, className = "mb-14 md:mb-20" }) {
  const center = align === "center";
  return (
    <div className={`max-w-4xl ${className} ${center ? "mx-auto text-center" : ""} ${dark ? "on-dark" : ""}`}>
      {(eyebrow || index) && (
        <p className={`eyebrow mb-5 flex items-center gap-3 ${center ? "justify-center" : ""}`}>
          {index && <span className={dark ? "text-white/40" : "text-slate-400"}>{index}</span>}
          {index && <span className={`h-px w-8 ${dark ? "bg-white/25" : "bg-slate-300"}`} aria-hidden="true" />}
          {eyebrow}
        </p>
      )}
      <h2 className={`h-section ${dark ? "text-white" : "text-ink"}`}>{title}</h2>
      {children && <p className="lede mt-6 max-w-2xl">{children}</p>}
    </div>
  );
}

/** Continuous keyword ticker. Items are duplicated for a seamless loop. */
export function Marquee({ items, className = "" }) {
  const row = (suffix) =>
    items.map((t, i) => (
      <li key={`${suffix}${i}`} className="flex items-center gap-10 whitespace-nowrap">
        <span>{t}</span>
        <span className="h-1 w-1 rounded-full bg-brand-light/70" aria-hidden="true" />
      </li>
    ));
  return (
    <div className={`overflow-hidden ${className}`} aria-hidden="true">
      <ul className="flex w-max animate-marquee gap-10 font-mono text-xs uppercase tracking-[0.22em] text-slate-400">
        {row("a")}
        {row("b")}
      </ul>
    </div>
  );
}

/** Engineering "crop mark" corners around media, like a drawing sheet. */
export function CropFrame({ children, label, className = "" }) {
  const tick = "absolute h-4 w-4 border-brand-light/70";
  return (
    <figure className={`relative ${className}`}>
      <span className={`${tick} -left-2 -top-2 border-l border-t`} aria-hidden="true" />
      <span className={`${tick} -right-2 -top-2 border-r border-t`} aria-hidden="true" />
      <span className={`${tick} -bottom-2 -left-2 border-b border-l`} aria-hidden="true" />
      <span className={`${tick} -bottom-2 -right-2 border-b border-r`} aria-hidden="true" />
      {children}
      {label && (
        <figcaption className="mt-4 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
          <span>{label}</span>
          <span aria-hidden="true">●</span>
        </figcaption>
      )}
    </figure>
  );
}
